export type SectionBenchmarkSummaryEntry = {
    /** Total count of ticks recorded for this section */
    count: number;

    /** Total duration of all ticks recorded for this section */
    totalDuration: number;

    /** Average duration per tick for this section */
    averageDuration: number;
};

/**
 * A summary of benchmark results for each section, where the key is the section name
 * and the value is the benchmark summary entry.
 */
export type SectionBenchmarkSummary<Section extends string | number = string> = Record<
    Section,
    SectionBenchmarkSummaryEntry
>;

type Stats<Section extends string | number = string> = Record<Section, {count: number; sum: number}>;

/**
 * Benchmark tool that summarizes 'ticks' by provided sections.
 * Each tick might either be the start or the end of a benchmark section.
 */
export class SectionBenchmarker<Section extends string | number = string> {
    private lastTick: number | undefined;
    private stats: Stats<Section> = {} as Stats<Section>;

    /**
     * If no section is given, it just sets the last tick.
     * Otherwise it records the duration since the last tick for the given section while also updating the last tick.
     *
     * @example
     * benchmarker.tick(); // start (not necessary when in a loop for example)
     * // ... some code to benchmark
     * benchmarker.tick("my_section"); // end and record duration for "my_section"
     */
    tick(section?: Section): void {
        const now = performance.now();
        if (this.lastTick !== undefined && section) {
            const duration = now - this.lastTick;

            const entry = this.stats[section] ?? {count: 0, sum: 0};
            entry.count++;
            entry.sum += duration;
            this.stats[section] = entry;
        }
        this.lastTick = now;
    }

    /**
     * Summarizes the benchmark results, providing count, total duration, and average duration for each section.
     * @returns A summary of benchmark results for each section.
     */
    summarize(): SectionBenchmarkSummary<Section> {
        const summary: SectionBenchmarkSummary<Section> = {} as SectionBenchmarkSummary<Section>;

        for (const section in this.stats) {
            const {count, sum} = this.stats[section];
            const totalDuration = sum;
            const averageDuration = totalDuration / count;
            summary[section as Section] = {count, totalDuration, averageDuration};
        }

        return summary;
    }
}
